import React from 'react';
import {
  Box, IconButton, SvgIcon,
  Typography
} from '@material-ui/core';

import { makeStyles } from '@material-ui/core/styles';

import FormikSelect from 'src/components/formik/FormikSelect';
import FormikCheckbox from 'src/components/formik/FormikCheckbox';
import Card from 'src/components/Card';
import FormikOrderableList from 'src/components/formik/FormikOrderableList';
import { PlusCircle, Trash2 } from 'react-feather';
import Checkbox from 'src/components/Checkbox';
import FormikConfigForm from 'src/components/formik/FormikConfigForm';
import FormikInput from 'src/components/formik/FormikInput';
import useGetRequest from 'src/hooks/useGetRequest';
import {
  RESOURCE_DETAILS_BY_RESOURCE_TYPE,
  RESOURCE_TYPE_OPTIONS,
  ENGAGEMENT_RESOURCE_TYPE_OPTIONS,
} from 'src/views/constants/tableRendererConstants';
import { ENGAGEMENT } from 'src/views/constants/perspectiveConstants';
import { Form } from 'src/types';
import toCamelCase from 'src/utils/toCamelCase';

const useStyles = makeStyles((theme) => ({
  columnCardCheckboxLabel: {
    margin: 0
  },
}));

const ColumnCard = ({
  isSelected,
  attribute,
  label,
  onChange
}: any) => {
  const classes = useStyles();
  return (
    <Card p={0}>
      <Box display="flex" alignItems="center">
        <Box pl={1}>
          <Checkbox
            // @ts-expect-error ts-migrate(2322) FIXME: Type '{ labelProps: { className: string; }; checke... Remove this comment to see the full error message
            labelProps={{
              className: classes.columnCardCheckboxLabel
            }}
            checked={isSelected}
            onChange={(e: any) => {
              onChange(attribute, e.target.checked);
            }}
          />
        </Box>
        <Box>
          <Typography variant="subtitle1">
            {label}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
};

const TableWidgetInspector = ({
  isOpen,
  workflowId,
  selectedWidget,
  saveWidget,
  deleteWidget,
  perspectiveType
}: any) => {
  const { result: table, isLoading } = useGetRequest({
    url: `/automatic/workflows/${workflowId}/table/`,
    initialResult: { fields: [] },
    // shouldFetchOnInitialRender: Boolean(workflowId)
  });
  const { result: forms } = useGetRequest({
    url: `/automatic/workflows/${workflowId}/forms/`,
    initialResult: [],
    // shouldFetchOnInitialRender: Boolean(workflowId)
  });

  if (isLoading) return null;

  // @ts-ignore
  const buildColumns = (formik: any) => RESOURCE_DETAILS_BY_RESOURCE_TYPE[formik.values.resourceType].buildColumns({
    fields: table.fields
  });

  const fieldsConfig = [
    {
      component: () => (
        <Typography variant="h5">
          Resource
        </Typography>
      ),
    },
    {
      name: 'resourceType',
      component: () => (
        <FormikSelect
          id="select-resource"
          fullWidth
          label="Resource"
          name="resourceType"
          options={perspectiveType === ENGAGEMENT ? ENGAGEMENT_RESOURCE_TYPE_OPTIONS : RESOURCE_TYPE_OPTIONS}
        />
      ),
    },
    {
      name: 'title',
      component: () => (
        <FormikInput
          fullWidth
          label="Title"
          name="title"
        />
      ),
      shouldRender: (formik: any) => formik.values.resourceType,
    },
    {
      component: () => (
        <Typography variant="h5">
          Filters
        </Typography>
      ),
    },
    {
      name: 'filters',
      component: (children: any) => <>{children}</>,
      isArray: true,
      isDynamic: true,
      fields: (index: any) => [
        {
          name: 'type',
          component: (_: any, formik: any) => (
            <FormikSelect
              label="Filter"
              name={`filters[${index}].type`}
              fullWidth
              // @ts-ignore
              options={RESOURCE_DETAILS_BY_RESOURCE_TYPE[formik.values.resourceType].filters}
            />
          ),
          gridProps: { xs: 9 },
        },
        {
          component: (_props: any) => (
            <Box display="flex">
              <IconButton onClick={() => _props.remove(index)}>
                <SvgIcon fontSize="small">
                  <Trash2 />
                </SvgIcon>
              </IconButton>
              <IconButton onClick={() => _props.push({})}>
                <SvgIcon fontSize="small">
                  <PlusCircle />
                </SvgIcon>
              </IconButton>
            </Box>
          ),
          shouldRender: (formik: any) => formik.values.filters[index],
          gridProps: { xs: 3 },
        },
      ],
      shouldRender: (formik: any) => formik.values.resourceType,
    },
    {
      name: 'columns',
      component: (_: any, formik: any) => (
        <Box display="flex" flexDirection="column">
          <Typography variant="h5">
            Columns
          </Typography>
          <Box pt={1}>
            <FormikOrderableList
              name="columns"
              options={buildColumns(formik).map((column: any) => ({
                id: column.attribute,
                content: (_props: any) => <ColumnCard {..._props} {...column} />
              }))}
            />
          </Box>
        </Box>
      ),
      shouldRender: (formik: any) => formik.values.resourceType,
      defaultValue: [],
    },
    {
      component: (children: any) => (
        <Box>
          <Typography variant="h5">
            Default sorting
          </Typography>
          <Box pt={2}>
            {children}
          </Box>
        </Box>
      ),
      shouldRender: (formik: any) => formik.values.resourceType,
      fields: [
        {
          name: 'defaultSortColumn',
          component: (_: any, formik: any) => (
            <FormikSelect
              name="defaultSortColumn"
              label="Column"
              options={formik.values.columns.filter((column: any) => buildColumns(formik).map((col: any) => col.attribute).includes(column)).map((column: any) => ({
                value: column,
                label: buildColumns(formik).find((col: any) => col.attribute === column).label
              }))}
            />
          ),
          gridProps: {
            md: 6,
            xs: 12
          }
        },
        {
          name: 'defaultSortDirection',
          component: (_: any, formik: any) => (
            <FormikSelect
              name="defaultSortDirection"
              label="Direction"
              options={[
                {
                  label: 'Ascending',
                  value: 'ASCENDING', // todo - constant
                },
                {
                  label: 'Descending',
                  value: 'DESCENDING', // todo - constant
                },
              ]}
            />
          ),
          gridProps: {
            md: 6,
            xs: 12
          }
        },
      ]
    },
    {
      component: (children: any) => (
        <Box>
          <Typography variant="h5">
            Table settings
          </Typography>
          <Box pl={2} pt={1}>{children}</Box>
        </Box>
      ),
      containerProps: { spacing: 0 },
      fields: [
        {
          name: 'isPaginated',
          component: () => (
            <FormikCheckbox
              label="Enable pagination"
              name="isPaginated"
              fullWidth
            />
          ),
        },
        {
          name: 'isSearchable',
          component: () => (
            <FormikCheckbox
              label="Enable search"
              name="isSearchable"
              fullWidth
            />
          ),
        },
        {
          name: 'isSortable',
          component: () => (
            <FormikCheckbox
              label="Enable sorting"
              name="isSortable"
              fullWidth
            />
          ),
        },
      ],
    },
    {
      component: (children: any) => (
        <Box display="flex" flexDirection="column">
          <Typography variant="h5">
            Resource creation
          </Typography>
          <Box pt={1}>
            {children}
          </Box>
        </Box>
      ),
      shouldRender: (formik: any) => formik.values.resourceType,
      fields: [
        {
          name: 'isCreateEnabled',
          component: () => (
            <FormikCheckbox
              label="Enable resource creation"
              name="isCreateEnabled"
              fullWidth
            />
          ),
        },
        {
          name: 'buttonLabel',
          component: () => (
            <FormikInput
              fullWidth
              label="Button label"
              name="buttonLabel"
            />
          ),
          defaultValue: 'Add new',
          shouldRender: (formik: any) => formik.values.isCreateEnabled,
        },
        {
          name: 'formId',
          component: () => (
            <FormikSelect
              name="formId"
              label="Form"
              options={forms.map((form: Form) => ({
                value: form.id,
                label: form.perspective.name
              }))}
            />
          ),
          defaultValue: 'Add new',
          shouldRender: (formik: any) => formik.values.isCreateEnabled,
        },
      ],
    },
  ];

  return (
    <FormikConfigForm
      result={selectedWidget.data}
      fieldsConfig={fieldsConfig}
      onSubmit={(widgetData: any, formActions: any) => {
        saveWidget(selectedWidget.id, { data: widgetData }).then(() => {
          formActions();
        });
      }}
      tertiaryButton={{
        label: 'Delete',
        onClick: () => deleteWidget(selectedWidget.id),
        look: 'error'
      }}
    />
  );
};

export default TableWidgetInspector;
